import { cn } from '@sohanemon/utils';
import { type ReactNode } from 'react';
import { type HighlightRenderNode, highlightCode } from './highlight';

const DIFF_SIGNS: Record<string, string> = {
	'diff-added': '+',
	'diff-removed': '-',
};

/**
 * Maps highlighter nodes onto React elements. Rendering the tree instead of the
 * generated HTML string keeps escaping inside React and lets diff lines own
 * their sign span.
 */
function renderHighlightNodes(
	nodes: HighlightRenderNode[],
	keyPrefix: string,
): ReactNode[] {
	return nodes.map((node, index) => {
		if (node.type === 'text') return node.value;

		const key = `${keyPrefix}-${index}`;
		const diffClass = node.classNames.find((name) => name in DIFF_SIGNS);
		const sign = diffClass ? DIFF_SIGNS[diffClass] : null;

		return (
			<span
				key={key}
				className={cn(node.classNames)}
				data-line={node.data?.line}
			>
				{sign ? <span className="diff-sign">{sign}</span> : null}
				{renderHighlightNodes(node.children, key)}
			</span>
		);
	});
}

export type CodeBlockFrameProps = {
	/** Header content: file name, language, icon… */
	header?: React.ReactNode;
	/** Header actions, e.g. a copy button. */
	actions?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
};

/**
 * Code block chrome: framed card and tab row. The body is supplied by the
 * caller so the same frame serves plain snippets and highlighted sources.
 */
export function CodeBlockFrame({
	header,
	actions,
	children,
	className,
}: CodeBlockFrameProps) {
	return (
		<div
			className={cn(
				'group corner-bevel relative overflow-hidden rounded border border-border bg-card font-mono shadow-sm',
				className,
			)}
		>
			<div className="flex items-center justify-between gap-2 border-border border-b bg-muted/60 py-1 ps-3 lg:ps-4 pe-1.5 lg:pe-2">
				<div className="flex min-w-0 items-center gap-2">{header}</div>
				{actions && (
					<div className="flex shrink-0 items-center gap-1">{actions}</div>
				)}
			</div>
			{children}
		</div>
	);
}

export type CodeBlockProps = {
	/** Raw source to render. */
	code: string;
	/** Language id, e.g. `tsx`. */
	lang?: string;
	/** Fence metadata line list, e.g. `2,4-6`. */
	highlight?: string;
	/** Force the line-number gutter on. */
	showLineNumbers?: boolean;
	className?: string;
} & Record<`data-${string}`, string | undefined>;

/** Highlighted code body — the same renderer the markdown fences use. */
export function CodeBlock({
	code,
	lang,
	highlight,
	showLineNumbers,
	className,
	...props
}: CodeBlockProps) {
	const source = code?.toString() ?? '';
	const lineCount = source.split('\n').length;
	const hasLineNumberVisible = Boolean(showLineNumbers) || lineCount > 3;

	// NOTE: synchronous on both sides — no server round trip, no idle hydration.
	const highlighted = highlightCode({
		code: source,
		highlight,
		lang: (lang || 'text').toLowerCase(),
		lineNumbers: hasLineNumberVisible,
	});

	return (
		<div className={cn('overflow-x-auto font-mono', className)} {...props}>
			<pre
				className={cn(
					'th-code',
					'py-2 lg:py-3',
					hasLineNumberVisible ? 'th-code--line-numbers px-0' : 'px-3 lg:px-4',
				)}
				data-language={highlighted.lang}
			>
				<code
					// NOTE: legacy parity — GitHub styles and user snippets select
					// `.language-*` on the inline code element.
					className={cn(highlighted.lang && `language-${highlighted.lang}`)}
				>
					{renderHighlightNodes(highlighted.nodes, 'n')}
				</code>
			</pre>
		</div>
	);
}
